import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";

import { logError } from "utility/Function";
import { platformGet, PlatformRestError } from "./platform";

export const DASHBOARD_ACCESS_PATH = "/api/v1/dashboard/access/";

type AccessResponse = {
  enabled: boolean;
  resilience?: boolean;
  engagement?: boolean;
  reports?: boolean;
  business_impact_sample?: boolean;
  banner?: boolean;
  // Absent from a Platform older than the web bundle.
  company?: DashboardCompany;
};

export type DashboardCompany = { id: number; name: string };

type Access = {
  enabled: boolean;
  resilience: boolean;
  engagement: boolean;
  reports: boolean;
  businessImpactSample: boolean;
  banner: boolean;
  company: DashboardCompany | null;
};

// Transport failures get a couple of retries before the answer is given up
// on. A single attempt meant one 502 during a deploy roll hid the tab for
// the rest of the session, indistinguishable from "not entitled".
const RETRY_DELAYS_MS = [500, 1500];

// The shapes a deploy roll produces — an expired session, or web running
// ahead of Platform — rather than anything a human should be paged about.
const UNREMARKABLE_STATUSES = [401, 404];

type AccessState = { access: Access | null; pending: boolean };

const AccessContext = createContext<AccessState>({
  access: null,
  pending: true,
});

/**
 * Whether the viewer's company may see the company dashboard.
 *
 * One fetch for the whole portal: the sidebar's tab, the route gate and
 * where `/client` lands all read it, and they must never disagree. A
 * given-up check reads as no, since the answer is an entitlement and an
 * unanswered question has to read as one.
 */
export function CompanyDashboardAccessProvider({
  children,
  retryDelaysMs = RETRY_DELAYS_MS,
}: {
  children: ReactNode;
  // Overridden only by tests, so the retry path runs without the wait. Held
  // in a ref so an inline array doesn't restart the check every render.
  retryDelaysMs?: number[];
}) {
  const [state, setState] = useState<AccessState>({
    access: null,
    pending: true,
  });
  const delaysRef = useRef(retryDelaysMs);
  delaysRef.current = retryDelaysMs;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = (attemptsSoFar: number): void => {
      platformGet<AccessResponse>(DASHBOARD_ACCESS_PATH)
        .then((answer) => {
          if (cancelled) return;
          const enabled = answer.enabled === true;
          setState({
            access: {
              enabled,
              resilience: enabled && answer.resilience === true,
              engagement: enabled && answer.engagement === true,
              reports: enabled && answer.reports === true,
              businessImpactSample:
                enabled && answer.business_impact_sample === true,
              banner: enabled && answer.banner === true,
              company: answer.company ?? null,
            },
            pending: false,
          });
        })
        .catch((error) => {
          if (cancelled) return;
          const status =
            error instanceof PlatformRestError ? error.status : undefined;

          // 403 is an answer, not a fault: the caller holds the scope but
          // administers no company. Believe it and stop retrying.
          if (status === 403) {
            setState({
              access: {
                enabled: false,
                resilience: false,
                engagement: false,
                reports: false,
                businessImpactSample: false,
                banner: false,
                company: null,
              },
              pending: false,
            });
            return;
          }

          const delays = delaysRef.current;
          if (attemptsSoFar < delays.length) {
            timer = setTimeout(
              () => attempt(attemptsSoFar + 1),
              delays[attemptsSoFar]
            );
            return;
          }

          setState({ access: null, pending: false });
          if (status === undefined || !UNREMARKABLE_STATUSES.includes(status)) {
            logError("Company dashboard access check failed", error);
          }
        });
    };

    attempt(0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <AccessContext.Provider value={state}>{children}</AccessContext.Provider>
  );
}

// `enabled` is `null` both while the check is in flight and once it has been
// given up on; `pending` tells those apart. `resilience` only hides the
// Employee Resilience tab; the page stays reachable by link. `engagement`
// picks the redesigned Engagement page over today's. `businessImpactSample`
// shows the Business Impact tab, which carries sample figures. `reports`
// shows the Reports Library tab. `banner` shows a banner above the
// dashboard at all. `company` is
// the company whose dashboard this is: the one the caller administers.
export function useCompanyDashboardAccess(): {
  enabled: boolean | null;
  resilience: boolean;
  engagement: boolean;
  reports: boolean;
  businessImpactSample: boolean;
  banner: boolean;
  company: DashboardCompany | null;
  pending: boolean;
} {
  const { access, pending } = useContext(AccessContext);
  return {
    enabled: access === null ? null : access.enabled,
    resilience: access?.resilience === true,
    engagement: access?.engagement === true,
    reports: access?.reports === true,
    businessImpactSample: access?.businessImpactSample === true,
    banner: access?.banner === true,
    company: access?.company ?? null,
    pending,
  };
}
